import { useState, useEffect, useCallback } from "react";
import DOMPurify from "dompurify";
import { useScrollReveal } from "../../../hooks/useScrollReveal";
import { Product } from "../../../mocks/products";

interface Props {
  product: Product;
}

interface AppCard {
  image: string;
  title: string;
  desc: string;
  isHtml?: boolean;
}

const buildApplicationCards = (product: Product): AppCard[] => {
  // Only show if admin has entered data in the "Applications" tab
  const apps = (product as unknown as { applications?: { image?: string; image_url?: string; title?: string; description?: string }[] }).applications;
  if (apps && apps.length > 0) {
    return apps.map((app) => {
      const imgSrc = app.image_url ?? app.image ?? product.images[0] ?? "";
      const desc = app.description ?? "";
      const isHtml = desc.includes("<");
      return {
        image: imgSrc,
        title: app.title ?? "",
        desc,
        isHtml,
      };
    });
  }

  // No applications data in admin → return empty (hide section)
  return [];
};

const ProductApplicationSection = ({ product }: Props) => {
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal(0.1);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const [bgVisible, setBgVisible] = useState(true);

  const cards = buildApplicationCards(product);

  const goTo = useCallback(
    (index: number) => {
      if (transitioning || cards.length <= 1) return;
      setTransitioning(true);
      setBgVisible(false);
      setTimeout(() => {
        setCurrentIndex(index);
        setBgVisible(true);
        setTransitioning(false);
      }, 500);
    },
    [transitioning, cards.length]
  );

  const goNext = useCallback(() => {
    goTo((currentIndex + 1) % cards.length);
  }, [currentIndex, cards.length, goTo]);

  const goPrev = useCallback(() => {
    goTo((currentIndex - 1 + cards.length) % cards.length);
  }, [currentIndex, cards.length, goTo]);

  // Auto-advance every 5s
  useEffect(() => {
    if (cards.length <= 1) return;
    const timer = setInterval(goNext, 5000);
    return () => clearInterval(timer);
  }, [goNext, cards.length]);

  if (cards.length === 0) return null;

  const current = cards[currentIndex];

  return (
    <section
      className="relative overflow-hidden"
      style={{ height: "600px" }}
    >
      {/* Background image — changes with slide */}
      <div
        className="absolute inset-0 transition-opacity duration-500"
        style={{ opacity: bgVisible ? 1 : 0 }}
      >
        {current.image ? (
          <img
            src={current.image}
            alt={current.title}
            className="w-full h-full"
            style={{
              objectFit: "cover",
              backgroundAttachment: "fixed",
            }}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#001428] to-[#004A99]" />
        )}
      </div>

      {/* Gradient overlay: transparent left → dark right */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(to right, rgba(0,10,30,0.15) 0%, rgba(0,10,30,0.55) 45%, rgba(0,10,30,0.82) 100%)",
        }}
      />

      {/* Content — right-aligned */}
      <div className="relative z-10 h-full flex items-center">
        <div className="max-w-[1400px] mx-auto px-8 w-full">
          <div className="flex justify-end">
            <div
              className="w-full max-w-[480px] text-right transition-all duration-500"
              style={{ opacity: bgVisible ? 1 : 0, transform: bgVisible ? "translateX(0)" : "translateX(20px)" }}
            >
              {/* Section label */}
              <div
                ref={headerRef}
                className={`transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
              >
                <p className="text-xs font-bold tracking-[0.3em] text-[#00AEEF] uppercase mb-3">
                  ỨNG DỤNG SẢN PHẨM
                </p>
              </div>

              {/* Slide counter */}
              <p className="text-white/40 text-sm mb-2 font-mono">
                {String(currentIndex + 1).padStart(2, "0")} / {String(cards.length).padStart(2, "0")}
              </p>

              {/* Title */}
              <h2 className="text-4xl font-bold text-white mb-4 leading-tight">
                {current.title}
              </h2>

              {/* Divider */}
              <div className="flex justify-end mb-4">
                <div className="w-12 h-1 bg-[#00AEEF] rounded-full" />
              </div>

              {/* Description */}
              {current.isHtml ? (
                <div
                  className="text-white/75 text-base leading-relaxed mb-8
                    [&_span]:text-white/75
                    [&_b]:font-bold [&_b]:text-white
                    [&_strong]:font-bold [&_strong]:text-white
                    [&_em]:italic
                    [&_p]:mb-2
                    [&_ul]:list-disc [&_ul]:pl-4 [&_ul>li]:mb-1
                    [&_ol]:list-decimal [&_ol]:pl-4 [&_ol>li]:mb-1"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(current.desc, {
                      ALLOWED_TAGS: ["span","b","strong","em","i","u","p","br","ul","ol","li","a"],
                      ALLOWED_ATTR: ["style","class","href","target","rel"],
                    }),
                  }}
                />
              ) : (
                <p className="text-white/75 text-base leading-relaxed mb-8">
                  {current.desc}
                </p>
              )}

              {/* Navigation */}
              {cards.length > 1 && (
                <div className="flex items-center justify-end gap-3">
                  <button
                    onClick={goPrev}
                    className="w-11 h-11 flex items-center justify-center bg-white/15 hover:bg-white/25 backdrop-blur-sm rounded-full border border-white/20 text-white transition-all cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line text-lg" />
                  </button>

                  {/* Dots */}
                  <div className="flex gap-1.5">
                    {cards.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => goTo(i)}
                        className={`rounded-full transition-all cursor-pointer ${
                          i === currentIndex
                            ? "w-6 h-2 bg-[#00AEEF]"
                            : "w-2 h-2 bg-white/30 hover:bg-white/50"
                        }`}
                      />
                    ))}
                  </div>

                  <button
                    onClick={goNext}
                    className="w-11 h-11 flex items-center justify-center bg-white/15 hover:bg-white/25 backdrop-blur-sm rounded-full border border-white/20 text-white transition-all cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line text-lg" />
                  </button>
                </div>
              )}

              {/* CTA */}
              <div className="mt-6">
                <a
                  href="#inquiry"
                  className="inline-flex items-center gap-2 bg-[#00AEEF] hover:bg-[#0099d4] text-white px-7 py-3 rounded-full font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-customer-service-2-line" />
                  Tư vấn ứng dụng phù hợp
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductApplicationSection;
